import { useEffect, useState } from "react";
import constants from "@/config/constants";
import useAuthStore from "@/hooks/useAuthStore";
import useDashboardStore from "@/hooks/useDashboardStore";
import useLangStore from "@/hooks/useLangStore";

interface FetchOptions {
    method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
    data?: Record<string, any>;
    formData?: FormData;
    query?: Record<string, any>;
    param?: string;
    signal?: AbortSignal;
}

function buildUrl(path: string, options: FetchOptions): string {
    if (path.startsWith("/")) path = path.slice(1);
    let url = `${constants.url}/${path}`;
    if (options.param) url += `/${options.param}`;
    if (options.query) {
        url +=
            "?" +
            Object.entries(options.query)
                .map(([k, v]) => `${k}=${v}`)
                .join("&");
    }
    return url;
}

function buildBody(data: Record<string, any>): { body: BodyInit; contentType?: string } {
    const hasFile = Object.values(data).some((v) => v instanceof File || v instanceof FileList);
    if (hasFile) return { body: objectToFormData(data) };
    return { body: JSON.stringify(data), contentType: "application/json" };
}

function objectToFormData(obj: Record<string, any>, fd = new FormData(), parentKey = ""): FormData {
    for (const [key, value] of Object.entries(obj)) {
        const formKey = parentKey ? `${parentKey}[${key}]` : key;
        if (value === null || value === undefined) continue;
        if (value instanceof File) fd.append(formKey, value);
        else if (value instanceof FileList) Array.from(value).forEach((f, i) => fd.append(`${formKey}[${i}]`, f));
        else if (Array.isArray(value)) {
            value.forEach((item, i) => {
                if (typeof item === "object" && !(item instanceof File)) objectToFormData(item, fd, `${formKey}[${i}]`);
                else fd.append(`${formKey}[${i}]`, item);
            });
        } else if (typeof value === "object") fd.append(formKey, JSON.stringify(value));
        else fd.append(formKey, String(value));
    }
    return fd;
}

export default function useFetch(path: string, baseOptions: FetchOptions = {}, init: boolean = true) {
    if (!path) throw new Error("Path is required for useFetch");

    const { token, updatePermissions } = useAuthStore();
    const { storeId } = useDashboardStore();
    const { lang } = useLangStore();

    const [pendingOptions, setPendingOptions] = useState<FetchOptions | null>(null);
    const [work, setWork] = useState(init ? 1 : 0);
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(init);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<number | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        const opts = { ...baseOptions, ...pendingOptions };

        async function fetching() {
            setLoading(true);
            setError(null);

            const url = buildUrl(path, opts);
            const headers: Record<string, string> = {};
            if (token) headers["Authorization"] = `Bearer ${token}`;
            if (storeId) headers["x-store-id"] = storeId;
            headers["x-language"] = lang || "en";

            const reqInit: RequestInit = {
                method: opts.method || "GET",
                mode: "cors",
                cache: "no-cache",
                headers,
                signal: opts.signal ?? controller.signal,
            };

            if (opts.formData) {
                reqInit.body = opts.formData;
            } else if (opts.data) {
                const { body, contentType } = buildBody(opts.data);
                reqInit.body = body;
                if (contentType) (reqInit.headers as Record<string, string>)["Content-Type"] = contentType;
            }

            try {
                const response = await fetch(url, reqInit);
                const json = await response.json();
                setStatus(response.status);
                if (response.ok) {
                    setData(json);
                    if (json.permissionsUpdated) {
                        updatePermissions(json.permissionsUpdated.store, json.permissionsUpdated.permissions);
                    }
                } else {
                    setError(json.message || "Request failed");
                }
            } catch (err: any) {
                if (err?.name !== "AbortError") setError("Network error, please try again");
            }

            setTimeout(() => setLoading(false), 300);
        }

        if (work > 0) fetching();
        return () => controller.abort();
    }, [work]);

    const invoke = (overrideOptions?: FetchOptions) => {
        if (overrideOptions) setPendingOptions(overrideOptions);
        setWork((prev) => prev + 1);
    };

    return { data, loading, error, status, invoke };
}
